/**
 * Editor draft model + client-side validation mirroring
 * backend/app/schemas/agent_config.py (ranges, requireds, cross-field rules)
 * plus the §6/§10 rules the backend enforces at the API layer (name unique
 * per user, router needs targets, handoff graph shares one engine).
 *
 * The backend remains the source of truth — this exists so almost every
 * save that leaves the browser succeeds.
 */

import type { Agent, AgentConfig, AgentRole, AgentStatus, AgentSummary } from "../../lib/types";
import { DEFAULT_AGENT_CONFIG, GEMINI_VOICES, REALTIME_VOICES } from "../../lib/types";
import {
  CONTEXT_BUDGET_WARN_TOKENS,
  KNOWLEDGE_INJECT_TOKEN_THRESHOLD,
  defaultVoiceId,
} from "./constants";

// ---------------------------------------------------------------------------
// Draft shape — everything the editor round-trips with the API
// ---------------------------------------------------------------------------

export interface AgentDraft {
  name: string;
  description: string;
  status: AgentStatus;
  role: AgentRole;
  config: AgentConfig;
}

/** Field-keyed inline errors. Keys match config/draft field names. */
export type DraftErrors = Partial<Record<string, string>>;

/** User-facing engine names — must match the card titles in EngineCards.tsx. */
const ENGINE_LABELS: Record<AgentConfig["voice_engine"], string> = {
  cascaded: "Standard",
  realtime: "Realtime (OpenAI)",
  gemini: "Natural (Google)",
};

/** Fresh draft for /agents/new — §6.3 default voice is "first in list". */
export function newDraft(): AgentDraft {
  return {
    name: "",
    description: "",
    status: "draft",
    role: "standalone",
    config: { ...DEFAULT_AGENT_CONFIG, voice_id: defaultVoiceId(DEFAULT_AGENT_CONFIG.tts_provider) },
  };
}

/** Draft from a loaded agent — fills any fields older configs may lack. */
export function draftFromAgent(agent: Agent): AgentDraft {
  return {
    name: agent.name,
    description: agent.description ?? "",
    status: agent.status,
    role: agent.role,
    config: { ...DEFAULT_AGENT_CONFIG, ...agent.config },
  };
}

/** Trim strings, drop empty list rows, null out empty optionals. */
export function normalizeDraft(draft: AgentDraft): AgentDraft {
  const config = draft.config;
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    status: draft.status,
    role: draft.role,
    config: {
      ...config,
      system_prompt: config.system_prompt.trim(),
      greeting: config.greeting.trim(),
      restrictions: config.restrictions.map((r) => r.trim()).filter(Boolean),
      end_call_phrases: config.end_call_phrases.map((p) => p.trim()).filter(Boolean),
      voice_id: config.voice_id?.trim() || null,
      phone_number_id: config.phone_number_id || null,
      handoff_description: config.handoff_description?.trim() || null,
    },
  };
}

// ---------------------------------------------------------------------------
// Validation — mirrors agent_config.py field-for-field
// ---------------------------------------------------------------------------

export function validateDraft(draft: AgentDraft, otherAgents: AgentSummary[]): DraftErrors {
  const errors: DraftErrors = {};
  const cfg = draft.config;

  // §6.1 identity (name/description are real columns)
  if (!draft.name) {
    errors.name = "Give the agent a name.";
  } else if (draft.name.length > 60) {
    errors.name = "Keep the name under 60 characters.";
  } else if (
    otherAgents.some((a) => a.name.trim().toLowerCase() === draft.name.toLowerCase())
  ) {
    errors.name = "You already have an agent with this name — pick another.";
  }
  if (draft.description.length > 200) {
    errors.description = "Keep the description under 200 characters.";
  }

  // §6.2 behavior
  if (!cfg.system_prompt) {
    errors.system_prompt = "The agent needs instructions — write a prompt or start from a template.";
  } else if (cfg.system_prompt.length > 8000) {
    errors.system_prompt = "The prompt is over the 8,000 character limit — trim it down.";
  }
  if (!cfg.greeting) {
    errors.greeting = "The agent speaks first — give it an opening line.";
  } else if (cfg.greeting.length > 300) {
    errors.greeting = "Keep the greeting under 300 characters.";
  }
  if (cfg.temperature < 0 || cfg.temperature > 1) {
    errors.temperature = "Temperature must be between 0 and 1.";
  }
  if (cfg.max_response_tokens < 16 || cfg.max_response_tokens > 1024) {
    errors.max_response_tokens = "Response length must be between 16 and 1024 tokens.";
  }

  // §6.3 voice — engine-dependent requireds
  if (cfg.voice_engine === "cascaded") {
    if (!cfg.voice_id) {
      errors.voice_id = "Pick a voice — choose from the list or paste a voice ID.";
    }
    if (cfg.speed < 0.7 || cfg.speed > 1.3) {
      errors.speed = "Speed must be between 0.7 and 1.3.";
    }
  } else if (cfg.voice_engine === "gemini") {
    if (!GEMINI_VOICES.includes(cfg.gemini_voice as (typeof GEMINI_VOICES)[number])) {
      errors.gemini_voice = "Pick one of the available Gemini voices.";
    }
  } else if (!REALTIME_VOICES.includes(cfg.realtime_voice as (typeof REALTIME_VOICES)[number])) {
    errors.realtime_voice = "Pick one of the available Realtime voices.";
  }

  // §6.4 conversation control
  if (cfg.interruption_min_words < 1 || cfg.interruption_min_words > 5) {
    errors.interruption_min_words = "Must be between 1 and 5 words.";
  }
  if (cfg.endpointing_ms < 100 || cfg.endpointing_ms > 1000) {
    errors.endpointing_ms = "Response speed must be between 100 and 1000ms.";
  }
  if (cfg.max_call_duration_s < 30 || cfg.max_call_duration_s > 3600) {
    errors.max_call_duration_s = "Call limit must be between 30 seconds and 1 hour.";
  }
  if (cfg.silence_timeout_s < 3 || cfg.silence_timeout_s > 60) {
    errors.silence_timeout_s = "Silence timeout must be between 3 and 60 seconds.";
  }

  // §6.5 knowledge
  if (cfg.retrieval_top_k < 1 || cfg.retrieval_top_k > 8) {
    errors.retrieval_top_k = "Passages per lookup must be between 1 and 8.";
  }

  // §6.6 telephony — outbound requires a number (agent_config.py cross-field)
  if (cfg.outbound_enabled && !cfg.phone_number_id) {
    errors.outbound_enabled =
      "Outgoing calls need a phone number — assign one above, or turn this off.";
  }

  // §6.7 multi-agent (§10 rules)
  if (draft.role === "router" && cfg.handoff_targets.length === 0) {
    errors.handoff_targets = "A router must have at least one agent to transfer to.";
  }
  const byId = new Map(otherAgents.map((a) => [a.id, a]));
  const mismatched = cfg.handoff_targets
    .map((id) => byId.get(id))
    .filter((a): a is AgentSummary => a !== undefined && a.voice_engine !== cfg.voice_engine);
  if (mismatched.length > 0) {
    const names = mismatched.map((a) => a.name).join(", ");
    const engineLabel = ENGINE_LABELS[cfg.voice_engine];
    errors.handoff_targets = `Transfers only work between agents on the same engine. This agent uses ${engineLabel}, but ${names} ${mismatched.length === 1 ? "does" : "do"} not.`;
  }
  if ((cfg.handoff_description?.length ?? 0) > 300) {
    errors.handoff_description = "Keep the handoff description under 300 characters.";
  }

  return errors;
}

// ---------------------------------------------------------------------------
// §6 validation rules: context-budget check — warn, never truncate
// ---------------------------------------------------------------------------

/** Rough spoken-text token estimate: ~4 chars per token. */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Returns a warning string when system prompt + restrictions + knowledge that
 * would be injected exceed the context budget, else null.
 */
export function contextBudgetWarning(
  draft: AgentDraft,
  readyKnowledgeTokens: number,
): string | null {
  const cfg = draft.config;
  const injects =
    cfg.knowledge_mode === "inject" ||
    (cfg.knowledge_mode === "auto" && readyKnowledgeTokens <= KNOWLEDGE_INJECT_TOKEN_THRESHOLD);
  const promptTokens =
    estimateTokens(cfg.system_prompt) + estimateTokens(cfg.restrictions.join("\n"));
  const total = promptTokens + (injects ? readyKnowledgeTokens : 0);
  if (total <= CONTEXT_BUDGET_WARN_TOKENS) return null;
  return `Your prompt, restrictions, and injected knowledge add up to roughly ${Math.round(total / 1000)}k tokens — past the model's comfortable context budget. Calls may fail or degrade. Switch Knowledge to "Search per question" or trim the documents.`;
}

// ---------------------------------------------------------------------------
// Section routing — which collapsible owns each error key (for auto-open)
// ---------------------------------------------------------------------------

export type SectionId =
  | "identity"
  | "behavior"
  | "voice"
  | "conversation"
  | "knowledge"
  | "telephony"
  | "multiagent";

const ERROR_SECTION: Record<string, SectionId> = {
  name: "identity",
  description: "identity",
  status: "identity",
  language: "identity",
  system_prompt: "behavior",
  greeting: "behavior",
  restrictions: "behavior",
  llm_model: "behavior",
  temperature: "behavior",
  max_response_tokens: "behavior",
  voice_engine: "voice",
  tts_provider: "voice",
  voice_id: "voice",
  speed: "voice",
  realtime_model: "voice",
  realtime_voice: "voice",
  gemini_model: "voice",
  gemini_voice: "voice",
  realtime_turn_detection: "voice",
  allow_interruptions: "conversation",
  interruption_min_words: "conversation",
  endpointing_ms: "conversation",
  max_call_duration_s: "conversation",
  silence_timeout_s: "conversation",
  end_call_phrases: "conversation",
  knowledge_mode: "knowledge",
  retrieval_top_k: "knowledge",
  phone_number_id: "telephony",
  inbound_enabled: "telephony",
  outbound_enabled: "telephony",
  record_calls: "telephony",
  role: "multiagent",
  handoff_targets: "multiagent",
  handoff_description: "multiagent",
};

/** Sections that need opening so every current error is visible. */
export function sectionsWithErrors(errors: DraftErrors): SectionId[] {
  const sections = new Set<SectionId>();
  for (const key of Object.keys(errors)) {
    const section = ERROR_SECTION[key];
    if (section) sections.add(section);
  }
  return [...sections];
}
